import type {ImageNodeGenerator, InputMaterialConnection, MaterialGraph} from "@cm/material-nodes"
import {BsdfPrincipled, Mapping, materialConnection, mul, RgbToBw, sub, UvMap} from "@cm/material-nodes"

export type DecalMaskData = {
    maskImage?: ImageNodeGenerator
    colorOverlayImage?: ImageNodeGenerator
    widthCm: number
    heightCm: number
    invert: boolean
}

function setUpMask(data: DecalMaskData) {
    const setUpMaskTextureNode = (image: ImageNodeGenerator) => {
        const uv = materialConnection(new UvMap({parameters: {inputs: {}, settings: {uvMapIndex: 0}}}), "uv")
        const normalizedUVs = materialConnection(
            new Mapping({parameters: {inputs: {vector: uv}, settings: {vectorType: "TEXTURE", scale: {x: data.widthCm, y: data.heightCm, z: 1}}}}),
            "vector",
        )
        return image.generator({uv: normalizedUVs, extension: "EXTEND", interpolation: "Linear", projection: "FLAT", size: [data.widthCm, data.heightCm]})
    }

    let maskValue: InputMaterialConnection<"float"> | number
    if (data.maskImage) {
        const maskTex = setUpMaskTextureNode(data.maskImage)
        // this handles both JPEG (RGB) and PNG (RGBA) cases:
        let maskLuminance: InputMaterialConnection<"color"> = materialConnection(
            new RgbToBw({
                parameters: {
                    inputs: {
                        color: maskTex.color,
                    },
                    settings: {},
                },
            }),
            "val",
        )
        if (data.invert) {
            maskLuminance = sub(1, maskLuminance)
        }
        maskValue = mul(maskLuminance, maskTex.alpha)
    } else {
        maskValue = 1.0
    }

    if (data.colorOverlayImage) {
        const overlayTex = setUpMaskTextureNode(data.colorOverlayImage)
        maskValue = mul(maskValue, overlayTex.alpha)
    }

    return maskValue
}

export function transformDecalMask(originalGraph: MaterialGraph, maskData: DecalMaskData): MaterialGraph {
    const graph = originalGraph.clone({cloneSubNode: () => true})
    for (const node of graph.nodes) {
        if (node instanceof BsdfPrincipled) {
            const alpha = node.parameters.inputs.alpha ?? node.parameters.settings.alpha ?? 1
            const mask = setUpMask(maskData)
            const multiplied = mul(alpha, mask)
            node.parameters.inputs.alpha = multiplied
        }
    }
    return graph
}
